import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number; // seconds
  expiresAtMs?: number;
}

export interface AuthUser {
  sub: string;
  email?: string;
  username?: string;
  groups: string[];
}

interface JwtPayload {
  sub?: string;
  email?: string;
  exp?: number;
  username?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
}

function decodeJwt<T = unknown>(token: string): T | null {
  try {
    const [, payload] = token.split('.');
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json))) as T;
  } catch {
    return null;
  }
}

function normalizeBase(url: string | undefined): string {
  return (url || '').replace(/\/+$/, '');
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly httpBackend = inject(HttpBackend);
  private readonly rawHttp = new HttpClient(this.httpBackend);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly ID_KEY = 'idToken';
  private readonly ACCESS_KEY = 'accessToken';
  private readonly REFRESH_KEY = 'refreshToken';
  private readonly EXPIRES_AT_KEY = 'expiresAtMs';

  private readonly refreshCheckIntervalMs = environment.AUTH_REFRESH_CHECK_INTERVAL_MS ?? 60_000;
  private readonly refreshLeewayMs = environment.AUTH_REFRESH_LEEWAY_MS ?? 300_000;
  private readonly refreshMaxRetryMs = environment.AUTH_REFRESH_MAX_RETRY_MS ?? 30_000;
  private readonly refreshInitialRetryMs = 1_000;
  private readonly refreshMaxStepMs = 10_000;
  private readonly authApiBase = normalizeBase(environment.AUTH_API_BASE_URL);

  private readonly _authCheckTick = signal(0);
  private readonly _user = signal<AuthUser | null>(null);
  private readonly _authResolved = signal(!this.isBrowser);
  private readonly _authRefreshing = signal(false);

  private initPromise: Promise<void> | null = null;
  private refreshPromise: Promise<AuthTokens | null> | null = null;

  user = this._user.asReadonly();
  authResolved = this._authResolved.asReadonly();
  authRefreshing = this._authRefreshing.asReadonly();

  isAuthenticated = computed(() => {
    this._authCheckTick();
    this._authResolved();
    return !!this._user() && this.hasValidStoredTokens();
  });

  isAdmin = computed(() =>
    (this._user()?.groups || []).some(g => g.toLowerCase() === 'admin')
  );

  constructor() {
    if (!this.isBrowser) return;

    void this.ensureInitialized();
    setInterval(() => {
      this.bumpAuthTick();
      void this.refreshIfNeeded();
    }, this.refreshCheckIntervalMs);
  }

  private now(): number {
    return Date.now();
  }

  private bumpAuthTick(): void {
    this._authCheckTick.update(v => v + 1);
  }

  authApiUrl(path = ''): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const prefix = this.authApiBase ? `${this.authApiBase}/api/auth` : '/api/auth';
    return `${prefix}${normalizedPath}`;
  }

  async ensureInitialized(): Promise<void> {
    if (!this.isBrowser) {
      this._authResolved.set(true);
      return;
    }

    if (this._authResolved()) return;

    if (!this.initPromise) {
      this.initPromise = this.initializeAuth().finally(() => {
        this._authResolved.set(true);
        this.bumpAuthTick();
      });
    }

    await this.initPromise;
  }

  private async initializeAuth(): Promise<void> {
    const stored = this.tokens();
    if (!stored?.idToken || !stored.accessToken) {
      this.clear();
      return;
    }

    if (this.hasValidStoredTokens()) {
      this.hydrateUserFromIdToken(stored.idToken);
      return;
    }

    if (!stored.refreshToken) {
      this._user.set(null);
      return;
    }

    await this.refreshNowSingleFlight(false);

    if (this.hasValidStoredTokens()) {
      const refreshed = this.tokens();
      if (refreshed?.idToken) {
        this.hydrateUserFromIdToken(refreshed.idToken);
      }
      return;
    }

    this._user.set(null);
  }

  async refreshIfNeeded(force = false): Promise<AuthTokens | null> {
    if (!this.isBrowser) return null;

    await this.ensureInitialized();

    const current = this.tokens();
    if (!current?.refreshToken) return null;

    const shouldRefresh = force || !this.hasValidStoredTokens(this.refreshLeewayMs);
    if (!shouldRefresh) return null;

    return this.refreshNowSingleFlight(force);
  }

  async getValidToken(preferIdToken = false): Promise<string | null> {
    if (!this.isBrowser) return null;

    await this.ensureInitialized();

    if (!this.hasValidStoredTokens(this.refreshLeewayMs)) {
      await this.refreshIfNeeded(true);
    }

    const current = this.tokens();
    if (!current?.idToken || !current.accessToken) return null;
    if (!this.isTokenValid(current.idToken) || !this.isTokenValid(current.accessToken)) return null;

    return preferIdToken ? current.idToken : current.accessToken;
  }

  async refreshNowSingleFlight(force = false): Promise<AuthTokens | null> {
    if (!this.isBrowser) return null;

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this._authRefreshing.set(true);

    this.refreshPromise = this.refreshWithRetry(force)
      .catch(() => null)
      .finally(() => {
        this._authRefreshing.set(false);
        this.refreshPromise = null;
        this.bumpAuthTick();
      });

    return this.refreshPromise;
  }

  private async refreshWithRetry(force: boolean): Promise<AuthTokens | null> {
    const start = this.now();
    let delayMs = this.refreshInitialRetryMs;

    while (true) {
      const attempt = await this.refreshOnce();
      if (attempt.kind === 'success') {
        return attempt.tokens;
      }

      if (attempt.kind === 'definitive_failure') {
        this.clear();
        return null;
      }

      const shouldRetry = this.now() - start < this.refreshMaxRetryMs;
      if (!shouldRetry && !force) {
        return null;
      }

      if (!shouldRetry && force) {
        return null;
      }

      await this.sleep(delayMs);
      delayMs = Math.min(delayMs * 2, this.refreshMaxStepMs);
    }
  }

  private async refreshOnce(): Promise<
    | { kind: 'success'; tokens: AuthTokens }
    | { kind: 'transient_failure' }
    | { kind: 'definitive_failure' }
  > {
    const current = this.tokens();
    if (!current?.refreshToken) {
      return { kind: 'definitive_failure' };
    }

    const user = this._user();
    const tokenPayload = decodeJwt<JwtPayload>(current.idToken);
    const username =
      user?.username ||
      tokenPayload?.['cognito:username'] ||
      tokenPayload?.username ||
      tokenPayload?.sub;
    const email = user?.email || tokenPayload?.email;
    const body: { refreshToken: string; username?: string; email?: string } = {
      refreshToken: current.refreshToken,
    };
    if (username) body.username = username;
    if (email) body.email = email;

    try {
      const response = await firstValueFrom(
        this.rawHttp.post<AuthTokens>(this.authApiUrl('/refresh'), body),
      );
      if (!response?.idToken || !response.accessToken) {
        return { kind: 'transient_failure' };
      }

      this.save({
        ...response,
        refreshToken: response.refreshToken || current.refreshToken,
      });

      const latest = this.tokens();
      if (!latest?.idToken || !latest.accessToken) {
        return { kind: 'transient_failure' };
      }

      return { kind: 'success', tokens: latest };
    } catch (error: unknown) {
      if (this.isDefinitiveRefreshFailure(error)) {
        return { kind: 'definitive_failure' };
      }
      return { kind: 'transient_failure' };
    }
  }

  private isDefinitiveRefreshFailure(error: unknown): boolean {
    const maybeError = error as {
      status?: number;
      error?: { error?: string; code?: string; message?: string };
      message?: string;
    };

    const status = maybeError?.status;
    if (status === 401 || status === 403) return true;

    const serverCode = `${maybeError?.error?.error || ''} ${maybeError?.error?.code || ''}`.toLowerCase();
    const message = `${maybeError?.error?.message || ''} ${maybeError?.message || ''}`.toLowerCase();
    const combined = `${serverCode} ${message}`;

    return (
      combined.includes('invalid_grant') ||
      combined.includes('invalid refresh') ||
      combined.includes('refresh token') ||
      combined.includes('token has expired') ||
      combined.includes('expiredtoken')
    );
  }

  private tokenExpiresAtMs(token: string): number | null {
    const payload = decodeJwt<JwtPayload>(token);
    if (typeof payload?.exp !== 'number') return null;
    return payload.exp * 1000;
  }

  /**
   * Ensures JWT has a valid `exp` claim that is still in the future.
   */
  private isTokenValid(token: string, leewayMs = 0): boolean {
    const expiresAt = this.tokenExpiresAtMs(token);
    if (!expiresAt) return false;
    return expiresAt - this.now() > leewayMs;
  }

  /**
   * Verifies required tokens exist and both are currently unexpired.
   */
  private hasValidStoredTokens(leewayMs = 0): boolean {
    const currentTokens = this.tokens();
    if (!currentTokens?.idToken || !currentTokens.accessToken) return false;
    return (
      this.isTokenValid(currentTokens.idToken, leewayMs) &&
      this.isTokenValid(currentTokens.accessToken, leewayMs)
    );
  }

  private hydrateUserFromIdToken(idToken: string): void {
    const payload = decodeJwt<JwtPayload>(idToken);
    if (!payload?.sub) {
      this._user.set(null);
      return;
    }

    const groups = Array.isArray(payload['cognito:groups'])
      ? payload['cognito:groups'].map(g => g.toLowerCase())
      : [];

    this._user.set({
      sub: payload.sub,
      email: payload.email,
      username: payload['cognito:username'] || payload.username || payload.sub,
      groups,
    });
  }

  private save(tokens: AuthTokens): void {
    if (!this.isBrowser) return;

    localStorage.setItem(this.ID_KEY, tokens.idToken);
    localStorage.setItem(this.ACCESS_KEY, tokens.accessToken);

    if (tokens.refreshToken) {
      localStorage.setItem(this.REFRESH_KEY, tokens.refreshToken);
    }

    if (tokens.expiresIn) {
      const expiresAtMs = this.now() + tokens.expiresIn * 1000;
      localStorage.setItem(this.EXPIRES_AT_KEY, String(expiresAtMs));
    }

    this.hydrateUserFromIdToken(tokens.idToken);
    this.bumpAuthTick();
  }

  tokens(): AuthTokens | null {
    if (!this.isBrowser) return null;

    const idToken = localStorage.getItem(this.ID_KEY);
    const accessToken = localStorage.getItem(this.ACCESS_KEY);

    if (!idToken || !accessToken) return null;

    const refreshToken = localStorage.getItem(this.REFRESH_KEY) || undefined;
    const expiresAtMs = Number(localStorage.getItem(this.EXPIRES_AT_KEY) || 0) || undefined;

    return { idToken, accessToken, refreshToken, expiresAtMs };
  }

  clear(): void {
    if (!this.isBrowser) return;

    localStorage.removeItem(this.ID_KEY);
    localStorage.removeItem(this.ACCESS_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);

    this._user.set(null);
    this.bumpAuthTick();
  }

  async login(email: string, password: string): Promise<AuthTokens | { challenge: string; session: string; username: string } | null> {
    const res = await firstValueFrom(
      this.http.post<any>(this.authApiUrl('/login'), { email, password }),
    );
    if (!res) return null;

    if (typeof res?.challenge === 'string' && res.challenge === 'NEW_PASSWORD_REQUIRED') {
      return res as { challenge: string; session: string; username: string };
    }

    this.save(res as AuthTokens);
    this._authResolved.set(true);
    return res as AuthTokens;
  }

  async refresh(): Promise<AuthTokens | null> {
    return this.refreshNowSingleFlight(true);
  }

  async logout(): Promise<void> {
    const t = this.tokens();
    try {
      if (t?.accessToken) {
        await firstValueFrom(this.http.post(this.authApiUrl('/logout'), { accessToken: t.accessToken }));
      }
    } catch {
      // Best effort only.
    }

    this.clear();
  }

  async changePassword(previousPassword: string, proposedPassword: string): Promise<{ ok: boolean } | undefined> {
    return firstValueFrom(
      this.http.post<{ ok: boolean }>(this.authApiUrl('/change-password'), {
        previousPassword,
        proposedPassword,
      }),
    );
  }

  async forgot(email: string): Promise<{ ok: boolean; codeDelivery?: unknown } | undefined> {
    return firstValueFrom(
      this.http.post<{ ok: boolean; codeDelivery?: unknown }>(this.authApiUrl('/forgot'), { email }),
    );
  }

  async confirmForgot(email: string, code: string, password: string): Promise<{ ok: boolean } | undefined> {
    return firstValueFrom(
      this.http.post<{ ok: boolean }>(this.authApiUrl('/confirm-forgot'), { email, code, password }),
    );
  }

  async completeNewPassword(email: string, newPassword: string, session: string): Promise<AuthTokens | undefined> {
    const res = await firstValueFrom(
      this.http.post<AuthTokens>(this.authApiUrl('/complete-new-password'), {
        email,
        newPassword,
        session,
      }),
    );

    if (res) {
      this.save(res);
      this._authResolved.set(true);
    }

    return res;
  }

  private async sleep(ms: number): Promise<void> {
    await new Promise<void>(resolve => setTimeout(resolve, ms));
  }
}
